import cn from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckboxInput } from 'src/shared/design-system/Input/CheckboxInput/CheckboxInput';
import { FormErrorMessage } from 'src/shared/design-system/Input/Error/FormErrorMessage';
import { DualLabeledTextInput } from 'src/shared/design-system/Input/LabeledTextInput/DualLabeledTextInput';
import { LabeledTextInput } from 'src/shared/design-system/Input/LabeledTextInput/LabeledTextInput';
import { Routes } from 'src/shared/Routes';
import styled from 'styled-components';
import { BaseInput, Checkbox } from 'web-common/src/shared/design-system/components/Input';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { useGateType } from './GateTypeContext';
import { useTermsConsentSentence } from './termsConsentSentence';

const useIsGate = () => useGateType() === 'page';

type InputChangeHandler = (event: React.FormEvent<HTMLInputElement>) => void;

export const OAuthErrorMessage = styled(FormErrorMessage)`
  margin-top: -${Spacing.HALF};
`;

const LegacyConsentText = styled.span`
  > a {
    color: ${Colors.GRAY_DARK_30} !important;
    font-weight: ${Font.Weight.BOLD};
    text-decoration: underline;
  }
`;

export const OAuthFieldset: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isGate = useIsGate();
  return (
    <fieldset className={cn('flex flex-col', { 'gap-2 border-none p-0': isGate, 'gap-4 p-6': !isGate })}>
      {children}
    </fieldset>
  );
};

interface OAuthNameFieldsProps {
  firstName: string;
  lastName: string;
  onFirstNameChange: InputChangeHandler;
  onLastNameChange: InputChangeHandler;
  errorMessage?: string;
  dataCyPrefix: string;
}

export const OAuthNameFields: React.FC<OAuthNameFieldsProps> = ({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  errorMessage,
  dataCyPrefix,
}) =>
  useIsGate() ? (
    <>
      <BaseInput
        label='First Name'
        name='firstname'
        value={firstName}
        onChange={onFirstNameChange}
        autoFocus
        data-cy={`${dataCyPrefix}-first-name`}
      />
      <BaseInput
        label='Last Name'
        name='lastname'
        value={lastName}
        onChange={onLastNameChange}
        errorMessage={errorMessage}
        hasError={!!errorMessage}
        data-cy={`${dataCyPrefix}-last-name`}
      />
    </>
  ) : (
    <DualLabeledTextInput
      topLabel='First name'
      topName='firstname'
      topValue={firstName}
      onTopChange={onFirstNameChange}
      bottomLabel='Last name'
      bottomName='lastname'
      bottomValue={lastName}
      onBottomChange={onLastNameChange}
      errorMessage={errorMessage}
      autoFocusTop
    />
  );

interface OAuthTextFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: InputChangeHandler;
  errorMessage?: string;
  dataCy: string;
}

export const OAuthTextField: React.FC<OAuthTextFieldProps> = ({ label, name, value, onChange, errorMessage, dataCy }) =>
  useIsGate() ? (
    <BaseInput
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      errorMessage={errorMessage}
      hasError={!!errorMessage}
      data-cy={dataCy}
    />
  ) : (
    <LabeledTextInput
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      errorMessage={errorMessage}
      data-cy={dataCy}
    />
  );

interface OAuthConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  dataCy: string;
  cta: string;
  legacyLinkClassName: string;
  /** Passing `null` is not the same as omitting: control wraps the checkbox only when this is defined. */
  error?: React.ReactNode;
}

export const OAuthConsent: React.FC<OAuthConsentProps> = ({
  checked,
  onChange,
  dataCy,
  cta,
  legacyLinkClassName,
  error,
}) => {
  const isGate = useIsGate();
  const sentence = useTermsConsentSentence(cta);

  const checkbox = isGate ? (
    <Checkbox
      name='tos'
      checked={checked}
      onChange={onChange}
      data-cy={dataCy}
      label={
        <span className='text-label-400 text-eggplant-300'>
          {sentence}
          <Link to={Routes.CONSUMER_TOS} target='_blank' className='font-normal text-eggplant-300 underline'>
            Terms of Service
          </Link>
          {', '}
          <Link to={Routes.CONSUMER_PRIVACY_POLICY} target='_blank' className='font-normal text-eggplant-300 underline'>
            Privacy Policy
          </Link>
          , and to receive text messages
        </span>
      }
    />
  ) : (
    <CheckboxInput checked={checked} onChange={() => onChange(!checked)} data-cy={dataCy}>
      <LegacyConsentText>
        {sentence}
        <Link to={Routes.CONSUMER_TOS} target='_blank' className={legacyLinkClassName}>
          Terms of Service
        </Link>
        {', '}
        <Link to={Routes.CONSUMER_PRIVACY_POLICY} target='_blank' className={legacyLinkClassName}>
          Privacy Policy
        </Link>
        , and to receive text messages
      </LegacyConsentText>
    </CheckboxInput>
  );

  if (isGate) {
    return (
      <div className='my-3.5'>
        {checkbox}
        {error}
      </div>
    );
  }

  return error === undefined ? (
    checkbox
  ) : (
    <div>
      {checkbox}
      {error}
    </div>
  );
};
